import {
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router-dom";

import { SkillSummary, SkillVersionRecord } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import {
  ResolvedSlug,
  activeSlugAtCursor,
  parseSlugs,
  resolveSlugs,
} from "@/shared/domain/skills/slugParse";
import { Goto } from "@/shared/navigation/Goto";

import styles from "./SkillSlugComposer.module.css";

/** Maximum suggestions shown in the autocomplete menu. */
const MAX_SUGGESTIONS = 8;

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Called on Enter (without shift). The composer does not own send eligibility. */
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Personal-tab skills used to drive autocomplete suggestions + slug resolution. */
  skills: SkillSummary[];
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}

/** Imperative API the parent (chat input toolbar) uses to drive autocomplete. */
export interface SkillSlugComposerHandle {
  /**
   * Reconcile autocomplete state against the textarea's current value + caret.
   * Call after programmatically inserting a slash (e.g., from the toolbar
   * skill button) so the popper opens even though no input event fired.
   */
  openAutocompleteAtCaret: () => void;
}

interface AutocompleteState {
  /** Offset of the leading `/` of the in-progress slug. */
  slashStart: number;
  /** Cursor position when the autocomplete opened — replace from slashStart..cursor on accept. */
  cursor: number;
  /** Filter query (substring after the slash). */
  query: string;
  /** Highlighted suggestion index in the filtered list. */
  highlight: number;
}

/**
 * Chat composer that supports inline ``/skill-name[@vN]`` slug tokens.
 *
 * Visible UI is a normal-looking textarea; what the user sees is actually a
 * mirror ``<div>`` rendered behind a transparent-text textarea, with detected
 * slugs wrapped in styled spans. Caret and selection come from the textarea;
 * coloring + click affordances come from the overlay. Autocomplete fires on
 * `/` and on subsequent slug-character keystrokes.
 *
 * Send eligibility is the parent's responsibility — this component only
 * surfaces ``onSubmit`` on Enter.
 */
export const SkillSlugComposer = React.forwardRef<
  SkillSlugComposerHandle,
  Props
>(function SkillSlugComposer(
  { value, onChange, onSubmit, placeholder, disabled, skills, textareaRef },
  forwardedRef,
) {
  const internalRef = React.useRef<HTMLTextAreaElement | null>(null);
  const setRef = (el: HTMLTextAreaElement | null) => {
    internalRef.current = el;
    if (textareaRef) {
      textareaRef.current = el;
    }
  };
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [autocomplete, setAutocomplete] =
    React.useState<AutocompleteState | null>(null);
  const [versionPopover, setVersionPopover] = React.useState<{
    anchorEl: HTMLElement;
    slug: ResolvedSlug;
  } | null>(null);
  // Per-suggestion-row version pick. Keys are skill_id; value `null` means
  // "Latest" and inserts `/name` (no @vN). The Latest case is the common path
  // for typed slug auto-acceptance — the version selector is opt-in.
  const [chosenVersions, setChosenVersions] = React.useState<
    Record<string, number | null>
  >({});

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      openAutocompleteAtCaret: () => {
        const ta = internalRef.current;
        if (ta === null) {
          return;
        }
        const cursor = ta.selectionEnd ?? ta.value.length;
        updateAutocompleteForCaret(ta.value, cursor);
      },
    }),
    // updateAutocompleteForCaret is a function-decl below; not in the deps
    // array because it's stable across renders. The handle only needs to read
    // the current ref + state at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Resolve all slugs in the current text. Memoized so the overlay re-render is
  // cheap on every keystroke.
  const resolvedSlugs: ResolvedSlug[] = React.useMemo(
    () => resolveSlugs(parseSlugs(value), skills),
    [value, skills],
  );

  const filteredSuggestions = React.useMemo(() => {
    if (autocomplete === null) {
      return [];
    }
    const q = autocomplete.query.toLowerCase();
    return skills
      .filter((s) => s.latest_version !== null)
      .filter((s) => s.skill.name.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [skills, autocomplete]);

  // Clamp the highlight index when the filter shrinks.
  React.useEffect(() => {
    if (autocomplete === null) {
      return;
    }
    if (autocomplete.highlight >= filteredSuggestions.length) {
      setAutocomplete((prev) =>
        prev === null
          ? null
          : { ...prev, highlight: Math.max(0, filteredSuggestions.length - 1) },
      );
    }
  }, [autocomplete, filteredSuggestions.length]);

  // Close the autocomplete on outside clicks. We can't rely on textarea blur
  // for this — clicking the per-row version Select shifts focus to it (so
  // blur fires) but we want to *keep* the popper open in that case. Carve
  // outs: the paper itself, the textarea, and any MUI nested popover
  // (Select's listbox is portaled to body and matches `[role="listbox"]`).
  React.useEffect(() => {
    if (autocomplete === null) {
      return;
    }
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target === null) {
        return;
      }
      if (paperRef.current?.contains(target)) {
        return;
      }
      if (internalRef.current?.contains(target)) {
        return;
      }
      if (target.closest('[role="listbox"], [role="menu"]')) {
        return;
      }
      setAutocomplete(null);
    };
    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [autocomplete]);

  function updateAutocompleteForCaret(text: string, cursor: number) {
    const active = activeSlugAtCursor(text, cursor);
    if (active === null) {
      setAutocomplete(null);
      return;
    }
    setAutocomplete((prev) => ({
      slashStart: active.slashStart,
      cursor,
      query: active.query,
      // Preserve the highlight index when the user types another character into
      // an already-open autocomplete — otherwise typing always jumps back to 0.
      // The clamp effect above keeps it valid as the filter shrinks.
      highlight: prev?.slashStart === active.slashStart ? prev.highlight : 0,
    }));
  }

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    const cursor = event.target.selectionEnd ?? event.target.value.length;
    updateAutocompleteForCaret(event.target.value, cursor);
  }

  function handleSelect(event: React.SyntheticEvent<HTMLTextAreaElement>) {
    // Caret moved without text change — refresh the autocomplete state.
    const ta = event.currentTarget;
    updateAutocompleteForCaret(ta.value, ta.selectionEnd ?? ta.value.length);
  }

  function acceptSuggestion(
    skillSummary: SkillSummary,
    versionOverride: number | null = chosenVersions[
      skillSummary.skill.skill_id
    ] ?? null,
  ) {
    if (autocomplete === null) {
      return;
    }
    // Two visible skills can share a name (private vs. org). When the user
    // explicitly picks one of those, encode the accessibility into the slug
    // so the send-time resolver honors their pick instead of falling back to
    // private-wins. Bare slugs stay uncluttered when there's no collision.
    const hasNameCollision = skills.some(
      (other) =>
        other.skill.skill_id !== skillSummary.skill.skill_id &&
        other.skill.name === skillSummary.skill.name,
    );
    const accessSuffix = hasNameCollision
      ? `(${skillSummary.skill.accessibility})`
      : "";
    const versionSuffix =
      versionOverride === null ? "" : `@v${versionOverride}`;
    const slug = `/${skillSummary.skill.name}${accessSuffix}${versionSuffix}`;
    const before = value.slice(0, autocomplete.slashStart);
    const after = value.slice(autocomplete.cursor);
    const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
    const insertion = slug + (needsTrailingSpace ? " " : "");
    const next = before + insertion + after;
    onChange(next);
    setAutocomplete(null);
    // Restore focus + caret after the inserted text.
    requestAnimationFrame(() => {
      const ta = internalRef.current;
      if (ta === null) {
        return;
      }
      const cursorPos = autocomplete.slashStart + insertion.length;
      ta.focus();
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (autocomplete !== null && filteredSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setAutocomplete({
          ...autocomplete,
          highlight: (autocomplete.highlight + 1) % filteredSuggestions.length,
        });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setAutocomplete({
          ...autocomplete,
          highlight:
            (autocomplete.highlight - 1 + filteredSuggestions.length) %
            filteredSuggestions.length,
        });
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        acceptSuggestion(filteredSuggestions[autocomplete.highlight]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setAutocomplete(null);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  }

  function syncOverlayScroll() {
    const ta = internalRef.current;
    const overlay = overlayRef.current;
    if (ta === null || overlay === null) {
      return;
    }
    overlay.scrollTop = ta.scrollTop;
    overlay.scrollLeft = ta.scrollLeft;
  }

  // The overlay rebuilds a list of {text, slug?} segments out of the source
  // string so React can render colored spans for the slug ranges. Walking the
  // resolved-slug list once is O(value.length) and keeps overlay text in
  // perfect lockstep with the textarea text (modulo CSS font matching).
  const overlaySegments = React.useMemo(() => {
    const segments: Array<{
      text: string;
      slug?: ResolvedSlug;
    }> = [];
    let cursor = 0;
    for (const slug of resolvedSlugs) {
      if (slug.start > cursor) {
        segments.push({ text: value.slice(cursor, slug.start) });
      }
      segments.push({ text: value.slice(slug.start, slug.end), slug });
      cursor = slug.end;
    }
    if (cursor < value.length) {
      segments.push({ text: value.slice(cursor) });
    }
    return segments;
  }, [value, resolvedSlugs]);

  function handleSlugClick(
    event: React.MouseEvent<HTMLElement>,
    slug: ResolvedSlug,
  ) {
    if (slug.skill === null) {
      return;
    }
    event.stopPropagation();
    setVersionPopover({ anchorEl: event.currentTarget, slug });
  }

  function applyVersionChange(slug: ResolvedSlug, version: number | null) {
    const skillName = slug.skill?.name ?? slug.name;
    // Preserve any existing accessibility disambiguator on the original slug
    // so re-pinning a version doesn't silently flip private↔org on a
    // collision name.
    const accessSuffix =
      slug.accessibility === null ? "" : `(${slug.accessibility})`;
    const versionSuffix = version === null ? "" : `@v${version}`;
    const replacement = `/${skillName}${accessSuffix}${versionSuffix}`;
    const next =
      value.slice(0, slug.start) + replacement + value.slice(slug.end);
    onChange(next);
    setVersionPopover(null);
  }

  // For the autocomplete popper anchor, build a virtual element that returns
  // the bounding rect of the slash character. Rebuilt on every render so the
  // popper repositions live as the user types.
  const autocompleteAnchor = React.useMemo(() => {
    if (autocomplete === null) {
      return null;
    }
    return {
      // Popper calls getBoundingClientRect on its own update cycle; we read
      // the textarea ref + computed mirror at call time so wrapping text and
      // window resizes are reflected without explicit value-deps churn.
      getBoundingClientRect: () => {
        const ta = internalRef.current;
        if (ta === null) {
          return new DOMRect(0, 0, 0, 0);
        }
        return getCaretRect(ta, autocomplete.slashStart);
      },
      // Popper expects an HTMLElement-shape; nodeType: 1 makes it satisfy the
      // duck-test without us actually returning a DOM node.
      nodeType: 1,
    } as unknown as HTMLElement;
  }, [autocomplete]);

  return (
    <div className={styles.composerRoot}>
      <div ref={overlayRef} className={styles.overlay} aria-hidden>
        {overlaySegments.map((segment, idx) => {
          if (segment.slug === undefined) {
            return <span key={idx}>{segment.text}</span>;
          }
          const isResolved = segment.slug.skill !== null;
          return (
            <span
              key={idx}
              className={
                isResolved ? styles.slugResolved : styles.slugUnresolved
              }
              onClick={(e) => handleSlugClick(e, segment.slug!)}
              role={isResolved ? "button" : undefined}
              tabIndex={isResolved ? 0 : undefined}
            >
              {segment.text}
            </span>
          );
        })}
        {/* Trailing zero-width so an empty/last-newline text still produces a row.
            Without this the overlay can shrink below the textarea on empty input. */}
        <span>&#8203;</span>
      </div>
      <textarea
        ref={setRef}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onScroll={syncOverlayScroll}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={styles.textarea}
        aria-label="Chat message input"
        rows={3}
      />
      <Popper
        open={autocomplete !== null && autocompleteAnchor !== null}
        anchorEl={autocompleteAnchor}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        className={styles.popper}
      >
        <Paper ref={paperRef} elevation={4} className={styles.suggestionsPaper}>
          {filteredSuggestions.length === 0 ? (
            <div className={styles.suggestionsEmpty}>
              <Typography
                variant="body2"
                className={styles.suggestionsEmptyText}
              >
                {skills.length === 0
                  ? "No skills in your Personal tab. Subscribe to a skill in skill settings to use slash commands."
                  : `No skills match “${autocomplete?.query ?? ""}”.`}
              </Typography>
            </div>
          ) : (
            <MenuList dense>
              {filteredSuggestions.map((summary, idx) => {
                const chosen = chosenVersions[summary.skill.skill_id] ?? null;
                return (
                  <MenuItem
                    key={summary.skill.skill_id}
                    selected={idx === autocomplete?.highlight}
                    onClick={() => acceptSuggestion(summary)}
                    onMouseEnter={() =>
                      setAutocomplete((prev) =>
                        prev === null ? null : { ...prev, highlight: idx },
                      )
                    }
                  >
                    <div className={styles.suggestionRow}>
                      <div className={styles.suggestionName}>
                        {summary.skill.name}
                      </div>
                      <div className={styles.suggestionDescription}>
                        {summary.latest_version?.description ?? ""}
                      </div>
                    </div>
                    <div
                      className={styles.versionPickerWrapper}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SuggestionVersionPicker
                        skillId={summary.skill.skill_id}
                        latestVersion={summary.latest_version?.version ?? null}
                        chosen={chosen}
                        onPick={(version) =>
                          setChosenVersions((prev) => ({
                            ...prev,
                            [summary.skill.skill_id]: version,
                          }))
                        }
                      />
                    </div>
                  </MenuItem>
                );
              })}
            </MenuList>
          )}
          <div className={styles.suggestionsFooter}>
            {filteredSuggestions.length > 0 && (
              <Typography variant="caption" className={styles.suggestionsHint}>
                ↑↓ to navigate · Enter or Tab to insert · Esc to dismiss
              </Typography>
            )}
            <Link
              to={Goto.route.AgentSkills}
              className={styles.suggestionsManageLink}
              onClick={() => setAutocomplete(null)}
            >
              Manage skills →
            </Link>
          </div>
        </Paper>
      </Popper>
      {versionPopover !== null && (
        <VersionPopover
          anchorEl={versionPopover.anchorEl}
          slug={versionPopover.slug}
          onClose={() => setVersionPopover(null)}
          onPick={(version) => applyVersionChange(versionPopover.slug, version)}
        />
      )}
    </div>
  );
});

interface VersionPopoverProps {
  anchorEl: HTMLElement;
  slug: ResolvedSlug;
  onClose: () => void;
  onPick: (version: number | null) => void;
}

/**
 * Tiny version-picker popover anchored to a highlighted slug span.
 *
 * Versions are loaded lazily on first open via :func:`useSkillVersions`. The
 * "Latest" option clears the explicit ``@vN`` pin so the slug falls back to
 * MAX(version) at resolution time.
 */
function VersionPopover({
  anchorEl,
  slug,
  onClose,
  onPick,
}: VersionPopoverProps) {
  const skillId = slug.skill?.skill_id;
  const versionsQuery = useSkillVersions(skillId);
  const versions: SkillVersionRecord[] = versionsQuery.data ?? [];

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      onMouseLeave={onClose}
    >
      <Paper elevation={4} className={styles.versionPopover}>
        <Typography variant="caption" className={styles.versionPopoverHeader}>
          {slug.skill?.name ?? slug.name}
        </Typography>
        <Select
          size="small"
          value={slug.version ?? "latest"}
          onChange={(e) => {
            const raw = e.target.value;
            onPick(raw === "latest" ? null : Number(raw));
          }}
          fullWidth
        >
          <MenuItem value="latest">Latest</MenuItem>
          {versions.map((v) => (
            <MenuItem key={v.version} value={v.version}>
              v{v.version}
            </MenuItem>
          ))}
        </Select>
      </Paper>
    </Popper>
  );
}

interface SuggestionVersionPickerProps {
  skillId: string;
  latestVersion: number | null;
  chosen: number | null;
  onPick: (version: number | null) => void;
}

/**
 * Inline version selector rendered to the right of each autocomplete row.
 *
 * Default value is "Latest" (which inserts `/skill-name` with no `@vN`
 * suffix); the user can switch to a specific pinned version before clicking
 * the row. The wrapping div in the parent MenuItem already stops click
 * propagation, so opening this picker doesn't double-fire as a row-accept.
 * The full version list is lazy-loaded only when the picker is actually
 * opened — one fetch per skill at most, and only if the user cares about
 * non-latest.
 */
function SuggestionVersionPicker({
  skillId,
  latestVersion,
  chosen,
  onPick,
}: SuggestionVersionPickerProps) {
  const [didOpen, setDidOpen] = React.useState(false);
  const versionsQuery = useSkillVersions(didOpen ? skillId : undefined);
  const versions = (versionsQuery.data ?? [])
    .map((v) => v.version)
    .filter((v) => v !== latestVersion);

  return (
    <Select
      size="small"
      variant="standard"
      disableUnderline
      value={chosen ?? "latest"}
      onOpen={() => setDidOpen(true)}
      onChange={(e) => {
        const raw = e.target.value;
        onPick(raw === "latest" ? null : Number(raw));
      }}
      // Sit above the suggestions paper, which is pinned to 1310 via
      // `.popper`. Without this the version dropdown renders behind the
      // paper and the hint bar (default Menu z-index is 1300).
      MenuProps={{ style: { zIndex: 1320 } }}
      className={styles.versionSelect}
    >
      <MenuItem value="latest">
        {latestVersion === null ? "—" : `v${latestVersion}`}
      </MenuItem>
      {versions.map((v) => (
        <MenuItem key={v} value={v}>
          v{v}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Compute the page-relative bounding rect of the character at ``position`` in
 * the textarea. Uses the standard mirror-div technique: an off-screen ``<div>``
 * copies the textarea's text-layout-relevant CSS, then a marker ``<span>`` at
 * the desired offset is measured via ``getBoundingClientRect``. The mirror is
 * removed before returning.
 */
function getCaretRect(
  textarea: HTMLTextAreaElement,
  position: number,
): DOMRect {
  const mirror = document.createElement("div");
  const computed = window.getComputedStyle(textarea);
  // Copy text-layout-relevant CSS via direct property assignment. setProperty
  // requires kebab-case names, so the obvious-looking `setProperty("boxSizing", …)`
  // is a silent no-op — which causes the mirror to fall back to default browser
  // metrics and the resulting caret position to drift from the real textarea
  // by several pixels per line. Direct assignment on .style accepts camelCase
  // and works the way you'd expect.
  mirror.style.boxSizing = computed.boxSizing;
  mirror.style.borderTopWidth = computed.borderTopWidth;
  mirror.style.borderRightWidth = computed.borderRightWidth;
  mirror.style.borderBottomWidth = computed.borderBottomWidth;
  mirror.style.borderLeftWidth = computed.borderLeftWidth;
  mirror.style.fontFamily = computed.fontFamily;
  mirror.style.fontSize = computed.fontSize;
  mirror.style.fontWeight = computed.fontWeight;
  mirror.style.fontStyle = computed.fontStyle;
  mirror.style.letterSpacing = computed.letterSpacing;
  mirror.style.lineHeight = computed.lineHeight;
  mirror.style.paddingTop = computed.paddingTop;
  mirror.style.paddingRight = computed.paddingRight;
  mirror.style.paddingBottom = computed.paddingBottom;
  mirror.style.paddingLeft = computed.paddingLeft;
  mirror.style.tabSize = computed.tabSize;
  mirror.style.textIndent = computed.textIndent;
  mirror.style.textTransform = computed.textTransform;
  mirror.style.wordSpacing = computed.wordSpacing;
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.left = "-9999px";
  mirror.style.top = "0";
  mirror.style.width = computed.width;
  mirror.style.height = "auto";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  mirror.style.overflow = "hidden";

  mirror.textContent = textarea.value.substring(0, position);
  const marker = document.createElement("span");
  // Non-empty content so the marker has a measurable box even at offset 0 or
  // immediately after a newline. The marker's left/top is what we return.
  marker.textContent = textarea.value.substring(position) || ".";
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const textareaRect = textarea.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  document.body.removeChild(mirror);

  const offsetX = markerRect.left - mirrorRect.left - textarea.scrollLeft;
  const offsetY = markerRect.top - mirrorRect.top - textarea.scrollTop;
  const top = textareaRect.top + offsetY;
  const left = textareaRect.left + offsetX;
  return {
    top,
    left,
    bottom: top + markerRect.height,
    right: left + markerRect.width,
    width: markerRect.width,
    height: markerRect.height,
    x: left,
    y: top,
    toJSON() {
      return this;
    },
  } as DOMRect;
}
